import SearchIcon from '@mui/icons-material/Search';
import { Box, InputBase, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import React, { useCallback, useId, useState } from 'react';

import { ActiveFiltersBar } from './ActiveFiltersBar';
import { AddFilterModal } from './AddFilterModal';
import { FilterBarProps } from './FilterBar.props';
import { FilterBarAddFilterButton } from './FilterBarAddFilterButton';
import { FilterBarBuiltInFields } from './FilterBarBuiltInFields';
import { FilterBarClearButton } from './FilterBarClearButton';
import { FilterBarQuickFilters } from './FilterBarQuickFilters';
import { FilterBarSearchBox } from './FilterBarSearchBox';
import { FilterBarSearchButton } from './FilterBarSearchButton';
import { filterStripSx, textFieldSx } from './filterBarStyles';
import { SEARCH_BUTTON_PENDING_HINT } from './searchButtonDirtyState';
import { useFilterBarState } from './useFilterBarState';

const DEFAULT_LABELS = {
  statusesDropdown: 'Status',
  jobTypesDropdown: 'Job Type',
  contractsDropdown: 'Contract',
  idField: 'Job ID',
  searchPlaceholder: 'Search all fields...',
  searchButtonAriaLabel: 'Search services',
} as const;

export const FilterBar: React.FC<FilterBarProps> = ({
  onSearch,
  statuses,
  statusGroups,
  jobTypes,
  contracts,
  contractNames = [],
  initialFilters,
  labels,
  fieldConfig,
  showBuiltInFields = true,
  showSearchBox = true,
  quickFilters,
  search,
  clearFilters,
}) => {
  // Config-driven slots replace the fixed Status/Job Type/Contract inputs
  // wholesale. Kept as a branch rather than a rewrite so every page still on
  // the fixed shape is untouched, and its tests stay green as the gate.
  const isConfigDriven = quickFilters !== undefined;
  // Collapse both APIs onto one notion of where the search box sits. The legacy
  // path only ever had "trailing" or "absent", so `showSearchBox` maps straight
  // onto it.
  const searchPlacement: 'leading' | 'trailing' | 'none' = isConfigDriven
    ? (search?.placement ?? 'trailing')
    : showSearchBox
      ? 'trailing'
      : 'none';
  const resolvedLabels = { ...DEFAULT_LABELS, ...labels };
  // The config-driven path defaults to the button's own visible text; only the
  // legacy Jobs-shaped path keeps "Search services".
  const searchButtonLabel = isConfigDriven
    ? (search?.buttonAriaLabel ?? labels?.searchButtonAriaLabel ?? 'Search')
    : resolvedLabels.searchButtonAriaLabel;
  const theme = useTheme();
  const pendingHintId = useId();
  const [modalOpen, setModalOpen] = useState(false);

  const {
    filters,
    recentlyUsed,
    isDirty,
    updateFilter,
    updateQuick,
    handleSearch,
    handleApplyFilter,
    handleEditFilter,
    handleRemoveFilter,
    handleClearAll,
  } = useFilterBarState({ onSearch, initialFilters, quickFilters, clearFilters });

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch]
  );

  const handleModalClose = useCallback(() => setModalOpen(false), []);
  const handleAddClick = useCallback(() => setModalOpen(true), []);

  // Resting styling for every text-shaped control. The slots layer their own
  // "engaged" state on top when they hold a value — see `textFieldSx`.
  const inputSx = textFieldSx(theme, false);
  const hasActiveFilters = filters.advancedFilters.length > 0;

  return (
    <Box>
      {/* `joined` gives up the bottom corners and edge when chips are showing,
          so the strip and the applied-filter row read as one card. */}
      <Box sx={filterStripSx(theme, hasActiveFilters)} onKeyDown={handleKeyDown}>
        {searchPlacement === 'leading' && (
          <FilterBarSearchBox
            config={search ?? {}}
            value={filters.multiSearch}
            onChange={(value) => updateFilter('multiSearch', value)}
            inputSx={inputSx}
          />
        )}

        {isConfigDriven ? (
          <FilterBarQuickFilters
            defs={quickFilters}
            quick={filters.quick}
            onChange={updateQuick}
            inputSx={inputSx}
          />
        ) : (
          showBuiltInFields && (
            <FilterBarBuiltInFields
              filters={filters}
              updateFilter={updateFilter}
              statuses={statuses}
              statusGroups={statusGroups}
              jobTypes={jobTypes}
              contracts={contracts}
              contractNames={contractNames}
              resolvedLabels={resolvedLabels}
              inputSx={inputSx}
              theme={theme}
            />
          )
        )}

        <FilterBarAddFilterButton onClick={handleAddClick} />

        <FilterBarClearButton onClear={handleClearAll} />

        {searchPlacement === 'trailing' &&
          (isConfigDriven ? (
            <FilterBarSearchBox
              config={search ?? {}}
              value={filters.multiSearch}
              onChange={(value) => updateFilter('multiSearch', value)}
              inputSx={inputSx}
            />
          ) : (
            <InputBase
              placeholder={resolvedLabels.searchPlaceholder}
              value={filters.multiSearch}
              onChange={(e) => updateFilter('multiSearch', e.target.value)}
              inputProps={{ 'aria-label': resolvedLabels.searchPlaceholder }}
              startAdornment={
                <SearchIcon
                  sx={{ fontSize: 15, mr: 0.5, color: theme.palette.v4.textFaint }}
                  aria-hidden
                />
              }
              sx={{
                ...textFieldSx(theme, filters.multiSearch.length > 0),
                width: 240,
                ml: 'auto',
              }}
            />
          ))}
        <FilterBarSearchButton
          onClick={handleSearch}
          ariaLabel={searchButtonLabel}
          isDirty={isDirty}
          pendingHintId={pendingHintId}
          hasTrailingSearchBox={searchPlacement === 'trailing'}
        />
        <Box id={pendingHintId} sx={visuallyHidden}>
          {SEARCH_BUTTON_PENDING_HINT}
        </Box>
      </Box>

      <ActiveFiltersBar
        filters={filters.advancedFilters}
        onRemove={handleRemoveFilter}
        onEdit={handleEditFilter}
        fieldDefinitions={fieldConfig?.fieldDefinitions}
        listOptions={fieldConfig?.listOptions}
      />

      <AddFilterModal
        open={modalOpen}
        onClose={handleModalClose}
        onApply={handleApplyFilter}
        recentlyUsed={recentlyUsed}
        fieldDefinitions={fieldConfig?.fieldDefinitions}
        categories={fieldConfig?.categories}
        listOptions={fieldConfig?.listOptions}
      />
    </Box>
  );
};
